import React, { useCallback, useMemo, useState } from 'react';
import { Card, Badge, Alert, Button, OverlayTrigger, Popover } from 'react-bootstrap';
import { useQueryClient } from '@tanstack/react-query';

import { DataTable } from './common/DataTable';
import type { ColumnConfig } from './common/DataTable/types';
import { FilterBar } from './common/FilterControls';
import type { FilterDefinition } from './common/FilterControls';
import { ApiErrorBanner } from './common/ApiErrorBanner';
import DatePeriodNavigation, { PeriodChangeEvent } from './common/DatePeriodNavigation';
import { InvoiceAttributionBadge } from './InvoiceAttributionBadge';
import InvoiceDetailModal from './InvoiceDetailModal';
import TransactionDetailModal from './TransactionDetailModal';
import { getBankTransactionsTransactionId } from '../api/generated/arrayscapeGamingDashboardAPI';
import type { CommissionableInvoiceRow, BankTransaction } from '../api/generated/model';

// Row is the unit this table iterates over. The underlying invoice is
// nested under .invoice; per-attribution + commission fields are on the
// row itself. Renamed locally for less code churn on field accesses.
type Row = CommissionableInvoiceRow;

// --- formatting helpers -------------------------------------------------

function formatCurrency(value: string | undefined): string {
  if (!value) return '$0.00';
  const num = parseFloat(value);
  if (isNaN(num)) return '$0.00';
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(num);
}

function formatLocalDate(s?: string): string {
  if (!s) return '';
  const d = new Date(s);
  if (isNaN(d.getTime())) return s;
  return d.toLocaleDateString();
}

// --- atom helpers used by columns --------------------------------------

const CheckIcon: React.FC<{ value?: boolean; muted?: boolean }> = ({ value, muted }) =>
  value ? <i className="bi bi-check-lg text-success"></i> : <i className={`bi bi-x-lg ${muted ? 'text-muted' : 'text-danger'}`}></i>;

const sourceLabel = (st?: string): string => {
  switch (st) {
    case 'bank_transactions': return 'Bank';
    case 'imported_commission_files': return 'Import';
    case 'mercury_transactions': return 'Mercury';
    case 'manual_commission_entries': return 'Manual';
    default: return st || '';
  }
};

// --- column builder -----------------------------------------------------

// Build the canonical Commissionable Sales column list. The Commission Paid
// column closes over `onOpenBankTxn` to open the bank-transaction detail
// modal for bank-sourced rows. The closure is fine because `onOpenBankTxn`
// is a stable useCallback ref — see memory feedback_datatable_renderCell_state
// for why renderCell must NOT close over component-state values that change
// after mount.
function buildInvoiceColumns(onOpenBankTxn: (btID: string) => void): ColumnConfig<Row>[] {
  return [
    {
      key: 'attribution_errors',
      label: '',
      width: 32,
      minWidth: 32,
      sortable: false,
      renderCell: (r) => <InvoiceAttributionBadge errors={r.invoice?.attribution_errors} iconOnly />,
    },
    { key: 'invoice_number', label: 'Invoice #', width: 90, sortable: true, renderCell: (r) => <>{r.invoice?.invoice_number}</> },
    { key: 'role', label: 'Role', width: 70, sortable: true, renderCell: (r) => <Badge bg={r.role === 'sales_lead' ? 'info' : 'primary'}>{r.role === 'sales_lead' ? 'Lead' : 'Sales'}</Badge> },
    { key: 'customer', label: 'Customer', width: 120, minWidth: 120, sortable: true, sortValue: (r) => r.invoice?.customer?.name || '', renderCell: (r) => <>{r.invoice?.customer?.name}</> },
    { key: 'booking_date', label: 'Booking Date', width: 100, sortable: true, renderCell: (r) => <>{formatLocalDate(r.invoice?.booking_date)}</> },
    { key: 'net_amount', label: 'Net Amount ($)', width: 110, sortable: true, renderCell: (r) => <span className="d-block text-end">{formatCurrency(r.invoice?.net_amount)}</span> },
    { key: 'net_revenue', label: 'Net Revenue ($)', width: 110, sortable: true, renderCell: (r) => <span className="d-block text-end">{formatCurrency(r.invoice?.net_revenue)}</span> },
    { key: 'payment_received', label: 'Payment Received', width: 120, sortable: true, renderCell: (r) => <span className="d-block text-end">{formatCurrency(r.invoice?.payment_received)}</span> },
    { key: 'balance_due', label: 'Balance Due', width: 110, sortable: true, renderCell: (r) => {
      const bal = parseFloat(r.invoice?.balance_due || '0');
      const cls = bal > 0 ? 'text-danger' : 'text-success';
      return <div className={`text-end ${cls}`}>{formatCurrency(r.invoice?.balance_due)}</div>;
    }, tooltip: (r) => `Outstanding balance on this invoice: ${formatCurrency(r.invoice?.balance_due)}` },
    { key: 'has_valid_invoice', label: 'Invoice', width: 60, sortable: true, renderCell: (r) => <div className="text-center"><CheckIcon value={r.invoice?.has_valid_invoice} /></div>, tooltip: () => 'Invoice has been reviewed and validated' },
    { key: 'has_valid_po', label: 'PO', width: 50, sortable: true, renderCell: (r) => <div className="text-center"><CheckIcon value={r.invoice?.has_valid_po} /></div>, tooltip: () => 'Valid purchase order or signed contract' },
    { key: 'has_vp_waiver', label: 'VP Waiver', width: 70, sortable: true, renderCell: (r) => <div className="text-center"><CheckIcon value={r.invoice?.has_vp_waiver} muted /></div>, tooltip: () => 'Written waiver from VP of Sales' },
    { key: 'delivery_ratio', label: 'Delivered', width: 75, sortable: true, renderCell: (r) => {
      const pct = Math.floor((r.invoice?.delivery_ratio || 0) * 100).toString();
      return <div className="text-center"><span className={pct === '100' ? 'text-success' : pct === '0' ? 'text-danger' : 'text-warning'}>{pct}%</span></div>;
    }, tooltip: (r) => `Delivery: ${Math.floor((r.invoice?.delivery_ratio || 0) * 100)}%` },
    { key: 'is_fully_approved', label: 'Approved', width: 75, sortable: true, renderCell: (r) => <div className="text-center">{r.invoice?.is_fully_approved ? <Badge bg="success">Yes</Badge> : <Badge bg="secondary">No</Badge>}</div>, tooltip: () => 'All criteria met' },
    {
      key: 'commission_status',
      label: 'Commission Status',
      width: 130,
      minWidth: 100,
      sortable: true,
      renderCell: (r) => {
        const status = r.commission_status;
        if (!status) return null;
        // Five-state pill from commissions_v2: not_due / due / paid /
        // overpaid / prepaid. Color semantics: success = settled,
        // warning = action still owed, danger = overpaid (operator
        // audit signal), info = prepaid (money out before sale
        // earnable), secondary = inert. Matches the diff page's
        // statusBadge in CommissionsDiffPage.tsx.
        const variant: Record<string, string> = {
          not_due: 'secondary',
          due: 'warning',
          paid: 'success',
          overpaid: 'danger',
          prepaid: 'info',
        };
        const label: Record<string, string> = {
          not_due: 'Not due',
          due: 'Due',
          paid: 'Paid',
          overpaid: 'Overpaid',
          prepaid: 'Prepaid',
        };
        return (
          <div className="text-center d-flex flex-column gap-1 align-items-center">
            <Badge bg={variant[status] || 'secondary'}>{label[status] || status}</Badge>
            {r.missing_quota && <Badge bg="danger">No Quota</Badge>}
          </div>
        );
      },
      tooltip: (r) => {
        const due = formatCurrency(r.commission_due);
        const paid = formatCurrency(r.commission_paid);
        const cleared = r.gates_cleared_at ? formatLocalDate(r.gates_cleared_at) : 'gates not all cleared';
        const nq = r.missing_quota ? ' · No quota for booking date' : '';
        return `Due: ${due} · Paid: ${paid} · ${cleared}${nq}`;
      },
    },
    {
      key: 'commission_due', label: 'Commission Due', width: 110, sortable: true,
      sortValue: (r) => parseFloat(r.commission_due || '0'),
      renderCell: (r) => <span className="d-block text-end">{formatCurrency(r.commission_due)}</span>,
      tooltip: () => 'Commission this user is due for this invoice (cumulative-tier walk for implicit; attribution_amount for explicit). Zero until all four gates clear.',
    },
    {
      key: 'commission_paid', label: 'Commission Paid', width: 170, minWidth: 130, sortable: true,
      sortValue: (r) => parseFloat(r.commission_paid || '0'),
      renderCell: (r) => {
        const amt = formatCurrency(r.commission_paid);
        const label = r.commission_payment_label;
        const st = r.commission_payment_source_type;
        const paid = parseFloat(r.commission_paid || '0');
        const payments = r.commission_payments ?? [];
        if (paid === 0 || !label) {
          return <span className="d-block text-end">{amt}</span>;
        }
        // Multi-payment case: render a popover listing every payment.
        // The total may be the sum of two or more BTs (over-quota
        // top-ups, retry payouts, etc.) — collapsing to "most recent"
        // hides earlier rows the operator may want to audit. Single
        // payment falls through to the original direct-click button so
        // routine cells stay one-click.
        if (payments.length > 1) {
          const popover = (
            <Popover id={`pay-pop-${r.attribution_id}`} className="commission-payments-popover">
              <Popover.Header as="div" className="small fw-semibold py-1">
                {payments.length} payments — {amt}
              </Popover.Header>
              <Popover.Body className="p-2">
                {payments.map((p, idx) => {
                  const pAmt = formatCurrency(p.allocated_amount);
                  const date = formatLocalDate(p.paid_at);
                  const slabel = p.source_label || '';
                  const slabelText = slabel ? sourceLabel(p.source_type) + ' — ' + slabel : sourceLabel(p.source_type);
                  if (p.source_type === 'bank_transactions' && slabel) {
                    return (
                      <Button
                        key={`${slabel}-${idx}`}
                        variant="link"
                        size="sm"
                        className="p-1 text-decoration-none d-flex justify-content-between align-items-center gap-3 w-100"
                        onClick={(e) => { e.stopPropagation(); onOpenBankTxn(slabel); }}
                        title={`Open ${slabelText}`}
                      >
                        <span className="small text-muted">{date}</span>
                        <span className="text-end">{pAmt}</span>
                      </Button>
                    );
                  }
                  return (
                    <div key={`${slabel || 'src'}-${idx}`} className="d-flex justify-content-between align-items-center gap-3 px-1 py-1 small">
                      <span className="text-muted">{date}</span>
                      <span title={slabelText}>{pAmt}</span>
                    </div>
                  );
                })}
              </Popover.Body>
            </Popover>
          );
          return (
            <OverlayTrigger trigger="click" placement="bottom-end" overlay={popover} rootClose>
              <Button
                variant="link"
                className="p-0 text-decoration-none d-flex align-items-center justify-content-between gap-2 w-100"
                onClick={(e) => e.stopPropagation()}
                title={`${payments.length} payments — click to see each`}
              >
                <Badge bg="secondary">{payments.length}×</Badge>
                <span>{amt}</span>
              </Button>
            </OverlayTrigger>
          );
        }
        if (st === 'bank_transactions') {
          return (
            <Button
              variant="link"
              className="p-0 text-decoration-none d-flex align-items-center justify-content-between gap-2 w-100"
              onClick={(e) => { e.stopPropagation(); onOpenBankTxn(label); }}
              title={`Open transaction ${label}`}
            >
              <Badge bg="secondary">{sourceLabel(st)}</Badge>
              <span>{amt}</span>
            </Button>
          );
        }
        return (
          <span className="d-flex align-items-center justify-content-between gap-2 w-100" title={label}>
            <Badge bg="secondary">{sourceLabel(st)}</Badge>
            <span>{amt}</span>
          </span>
        );
      },
      tooltip: (r) => {
        const base = 'Sum of commission payments allocated to this invoice for this user/role.';
        const count = r.commission_payments?.length ?? 0;
        if (count > 1) return `${base}\nClick to see all ${count} payments.`;
        if (!r.commission_payment_label) return base;
        return `${base}\nMost recent: ${sourceLabel(r.commission_payment_source_type)} — ${r.commission_payment_label}`;
      },
    },
    {
      key: 'commission_last_paid_at', label: 'Last Paid', width: 100, sortable: true,
      renderCell: (r) => <>{formatLocalDate(r.commission_last_paid_at || undefined)}</>,
      tooltip: () => 'paid_at of the most recent commission payment for this invoice.',
    },
  ];
}

// Re-export the formatting helpers so callers (e.g. the team-aggregate
// extra columns in CommissionsPage) can format their own cells
// consistently with the standard columns.
export { formatCurrency, formatLocalDate };

// --- public component ---------------------------------------------------

export interface CommissionableInvoicesTableProps {
  /**
   * Already-loaded period rows (one per attribution). The component does
   * not fetch — the parent owns the query (typically usePaginatedQuery)
   * so the same component can serve both the per-user view and the
   * cross-team Sales view.
   */
  invoices: Row[];
  /** Loading flag from the parent's query — controls the spinner state. */
  isLoading: boolean;
  /** Error from the parent's query (e.g. usePaginatedQuery's `error`).
   *  Surfaced via a danger-style banner above the table so failed
   *  requests aren't silently swallowed. */
  error?: Error | null;
  /** Per-user DataTable preferences. */
  preferencesKey: string;
  userId?: string;
  userPreferences?: any;
  /**
   * Extra columns appended after the standard column set. The Sales page
   * uses this for the Salesperson + Sales Lead columns. Per memory
   * feedback_datatable_renderCell_state these renderCell functions MUST
   * read everything they need from the row (`inv`) — closures over
   * component-state will silently break.
   */
  extraColumns?: ColumnConfig<Row>[];
  /**
   * Optional callback fired when the modal updates an invoice (gate flag
   * flip, etc.). Parent should invalidate its query so commission_status /
   * due / paid / gates_cleared_at refresh — the PATCH response carries
   * only the canonical Invoice fields, not the per-row commission state.
   */
  onInvoiceUpdated?: () => void;
  /** React-query key prefix for the modal's invoice cache invalidation. */
  invoiceQueryKey?: unknown[];
  /** Total row count from the cursor's first-page summary. Drives the
   *  "X of Y" label when filters reduce the visible rows. */
  totalCount?: number;
  /** Pagination state from the parent's usePaginatedQuery. */
  pagination?: { hasMore: boolean; limit: number };
  /** Load-next-page handler from the parent's usePaginatedQuery. */
  onLoadMore?: () => void;
  /**
   * Row identity. Pass the SAME function the page passes to its
   * usePaginatedQuery getRowKey so dedup-detection and React keys agree.
   * Required because rows here are (invoice, role, attribution_name)
   * tuples and the right composite varies by caller (per-user vs team).
   */
  getRowKey: (r: Row) => string;

  /**
   * Optional Salesperson / Sales Lead filter chips, rendered inline in
   * this table's Filters bar. Values + callback are owned by the parent
   * because these are sent to the API as query params (LIKE search) —
   * changing them re-fetches the page. Used by the team Sales view.
   */
  salesperson?: string;
  salesLead?: string;
  onSalespersonChange?: (value: string | undefined) => void;
  onSalesLeadChange?: (value: string | undefined) => void;

  /**
   * Search / commission_status / role filters. Controlled props (parent
   * owns the value + setter) because each one drives an API query
   * param — the table is paginated, so client-side filtering would
   * silently drop matches beyond the first page. Mirror the
   * salesperson / salesLead pattern: change handler runs on every
   * keystroke / multiselect toggle, and the parent debounces or folds
   * into its query params as it sees fit.
   */
  search?: string;
  commissionStatus?: string;
  role?: string;
  onSearchChange?: (value: string | undefined) => void;
  onCommissionStatusChange?: (value: string | undefined) => void;
  onRoleChange?: (value: string | undefined) => void;

  /**
   * Role filter mode. 'multi' (default) renders the legacy multi-select
   * chip — operator can pick any subset of {Sales, Lead}. 'single-required'
   * renders a non-clearable single-select; the parent must always pass a
   * non-empty role value. Used on the team-aggregate view where the
   * backend rejects blank/multi role (see commissions_invoices_cursor.go's
   * IsTeam validation).
   */
  roleFilterMode?: 'multi' | 'single-required';

  /**
   * Optional in-header DatePeriodNavigation. Mirrors the standard
   * DataTable header pattern (see feedback_datatable_header.md). When
   * provided, the table renders the picker in its Card.Header pill
   * alongside Filters; the parent owns the values + handler so they
   * flow into buildQueryParams. Pass all three (initialDateRange,
   * initialPeriod, onPeriodChange) or none.
   */
  showDateNav?: boolean;
  initialDateRange?: { start_date: string; end_date: string };
  initialPeriod?: string;
  onPeriodChange?: (event: PeriodChangeEvent) => void;
  /** Optional title shown to the left of the date picker, e.g. "Sales for". */
  title?: string;
  titleIcon?: string;

  /**
   * Optional content rendered immediately to the right of the Filters
   * button in the Card.Header. Use for view-level switchers (the
   * Projection picker on the team-aggregate view) that should be at
   * peer level with Filters / DateNav, not above the whole table.
   */
  headerExtra?: React.ReactNode;

  /**
   * Sort state. Controlled by the parent so column-header clicks drive
   * a server-side sort via the cursor query params — per
   * feedback_paginated_table_filters, client-side sorting a paginated
   * set silently shuffles only the loaded subset. Only column keys
   * present in commissions_invoices_cursor.go sortRows produce a
   * meaningful order; the columns marked `sortable: true` above are
   * exactly that set.
   */
  sortField: string;
  sortDirection: 'asc' | 'desc';
  onSortChange: (field: string, direction: 'asc' | 'desc') => void;
}

const CommissionableInvoicesTable: React.FC<CommissionableInvoicesTableProps> = ({
  invoices,
  isLoading,
  error,
  preferencesKey,
  userId,
  userPreferences,
  extraColumns,
  onInvoiceUpdated,
  invoiceQueryKey,
  totalCount,
  pagination,
  onLoadMore,
  getRowKey,
  salesperson,
  salesLead,
  onSalespersonChange,
  onSalesLeadChange,
  search,
  commissionStatus,
  role,
  onSearchChange,
  onCommissionStatusChange,
  onRoleChange,
  roleFilterMode = 'multi',
  showDateNav,
  initialDateRange,
  initialPeriod,
  onPeriodChange,
  title,
  titleIcon,
  headerExtra,
  sortField,
  sortDirection,
  onSortChange,
}) => {
  const queryClient = useQueryClient();

  // Modal state — InvoiceDetailModal opens on row click; bank-txn modal
  // opens from the Commission Paid cell click on bank-sourced rows.
  const [invoiceModalId, setInvoiceModalId] = useState<string | null>(null);
  const [selectedBankTxn, setSelectedBankTxn] = useState<BankTransaction | null>(null);

  const openBankTxnModal = useCallback(async (btID: string) => {
    try {
      const bt = await getBankTransactionsTransactionId(btID);
      setSelectedBankTxn(bt as BankTransaction);
    } catch (err: any) {
      window.alert(`Failed to load bank transaction ${btID}: ${err?.response?.data?.message || err?.message || err}`);
    }
  }, []);

  const columns = useMemo(() => {
    const base = buildInvoiceColumns(openBankTxnModal);
    return extraColumns && extraColumns.length > 0 ? [...base, ...extraColumns] : base;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openBankTxnModal, extraColumns]);

  // All filters are controlled by the parent now — there is no local
  // filter state. Each value drives an API query param so the cursor
  // applies it server-side; rolling our own client-side filter would
  // silently drop matches beyond the first page.
  const [showFilters, setShowFilters] = useState(
    !!(salesperson || salesLead || search || commissionStatus || role),
  );
  const handleFilterChange = useCallback((field: string, value: string | undefined) => {
    // Text filters collapse '' → undefined (empty input == no filter).
    // Multiselect filters preserve '' as the "deselect all / match nothing"
    // sentinel — collapsing it would make Deselect all behave like Select all.
    switch (field) {
      case 'salesperson':
        onSalespersonChange?.(value || undefined);
        break;
      case 'sales_lead':
        onSalesLeadChange?.(value || undefined);
        break;
      case 'search':
        onSearchChange?.(value || undefined);
        break;
      case 'commission_status':
        onCommissionStatusChange?.(value);
        break;
      case 'role':
        onRoleChange?.(value);
        break;
    }
  }, [onSalespersonChange, onSalesLeadChange, onSearchChange, onCommissionStatusChange, onRoleChange]);
  const handleFiltersClear = useCallback(() => {
    onSalespersonChange?.(undefined);
    onSalesLeadChange?.(undefined);
    onSearchChange?.(undefined);
    onCommissionStatusChange?.(undefined);
    onRoleChange?.(undefined);
  }, [onSalespersonChange, onSalesLeadChange, onSearchChange, onCommissionStatusChange, onRoleChange]);
  const activeFilterCount = useMemo(
    () =>
      (salesperson ? 1 : 0) +
      (salesLead ? 1 : 0) +
      (search ? 1 : 0) +
      (commissionStatus ? 1 : 0) +
      (role ? 1 : 0),
    [salesperson, salesLead, search, commissionStatus, role],
  );
  const filtersForBar = useMemo(
    () => ({
      salesperson,
      sales_lead: salesLead,
      search,
      commission_status: commissionStatus,
      role,
    }),
    [salesperson, salesLead, search, commissionStatus, role],
  );
  // Each filter is only rendered when the parent provided its change
  // handler — this lets a per-user page omit role/sales* without
  // showing dead controls.
  const filterDefs: FilterDefinition[] = useMemo(() => {
    const defs: FilterDefinition[] = [];
    if (onCommissionStatusChange) {
      defs.push({
        type: 'multiselect', field: 'commission_status', label: 'Status', icon: 'bi-flag',
        // Five-state set from the commissions_v2 engine — see the pill
        // renderer at the top of this file for color semantics. The
        // legacy three-state collapse (partially_due / partial /
        // partially_paid) dropped at the Phase 3 cutover.
        options: [
          { value: 'not_due', label: 'Not due' },
          { value: 'due', label: 'Due' },
          { value: 'paid', label: 'Paid' },
          { value: 'overpaid', label: 'Overpaid' },
          { value: 'prepaid', label: 'Prepaid' },
        ],
      });
    }
    if (onRoleChange) {
      if (roleFilterMode === 'single-required') {
        defs.push({
          type: 'select', field: 'role', label: 'Role', icon: 'bi-person',
          required: true,
          options: [
            { value: 'salesperson', label: 'Sales' },
            { value: 'sales_lead', label: 'Lead' },
          ],
        });
      } else {
        defs.push({
          type: 'multiselect', field: 'role', label: 'Role', icon: 'bi-person',
          options: [
            { value: 'salesperson', label: 'Sales' },
            { value: 'sales_lead', label: 'Lead' },
          ],
        });
      }
    }
    if (onSearchChange) {
      defs.push({
        type: 'text', field: 'search', label: 'Search', icon: 'bi-search',
        placeholder: 'Invoice #, customer, or attribution name…',
      });
    }
    if (onSalespersonChange) {
      defs.push({
        type: 'text', field: 'salesperson', label: 'Salesperson', icon: 'bi-person',
        placeholder: 'Filter by salesperson…',
      });
    }
    if (onSalesLeadChange) {
      defs.push({
        type: 'text', field: 'sales_lead', label: 'Sales Lead', icon: 'bi-person-badge',
        placeholder: 'Filter by sales lead…',
      });
    }
    return defs;
  }, [onCommissionStatusChange, onRoleChange, roleFilterMode, onSearchChange, onSalespersonChange, onSalesLeadChange]);

  return (
    <Card className="d-flex flex-column flex-grow-1" style={{ minHeight: 0, overflow: 'hidden' }}>
      <Card.Header className="bg-primary text-white flex-shrink-0">
        <div className="d-flex justify-content-between align-items-center m-1 flex-wrap gap-2">
          <div className="d-flex align-items-center gap-2 flex-wrap">
            <div className="bg-header-control rounded px-2 py-1 d-flex align-items-center gap-2">
              {title && (
                <span className="text-primary fw-semibold">
                  {titleIcon && <i className={`bi ${titleIcon} me-1`}></i>}
                  {title}
                </span>
              )}
              {showDateNav && onPeriodChange && (
                <>
                  <DatePeriodNavigation
                    onPeriodChange={onPeriodChange}
                    dateFormat="date-only"
                    initialNavigationMode={initialPeriod as any}
                    initialDateRange={initialDateRange}
                  />
                  <div className="border-end border-primary mx-1" style={{ height: '24px' }}></div>
                </>
              )}
              <Button
                variant={activeFilterCount > 0 ? 'primary' : 'outline-primary'}
                size="sm"
                onClick={() => setShowFilters(!showFilters)}
              >
                <i className="bi bi-funnel me-1"></i>
                Filters
                {activeFilterCount > 0 && (
                  <span className="badge bg-header-control text-primary ms-1">{activeFilterCount}</span>
                )}
              </Button>
              {headerExtra}
            </div>
          </div>
          <div className="d-flex align-items-center gap-2 flex-wrap">
            <div className="bg-header-control rounded px-3 py-1">
              <span className="text-primary">
                {(() => {
                  // Filtering happens server-side; totalCount (from the
                  // cursor summary) reflects the filtered total. Loaded
                  // count is uninteresting to the operator — they just
                  // want the row total.
                  const total = totalCount ?? invoices.length;
                  return `${total} ${total === 1 ? 'invoice' : 'invoices'}`;
                })()}
              </span>
            </div>
          </div>
        </div>
        {showFilters && (
          <FilterBar
            filters={filtersForBar}
            filterDefs={filterDefs}
            onFilterChange={handleFilterChange}
            onClearFilters={handleFiltersClear}
          />
        )}
      </Card.Header>
      <ApiErrorBanner error={error} />
      <Card.Body className="flex-grow-1 overflow-hidden p-0" style={{ minHeight: 0 }}>
        <DataTable
          data={invoices}
          columns={columns}
          getRowKey={getRowKey}
          onRowClick={(r) => r.invoice?.id && setInvoiceModalId(r.invoice.id)}
          sortConfig={{ field: sortField, direction: sortDirection }}
          onSortChange={(field, dir) => onSortChange(field, dir as 'asc' | 'desc')}
          preferencesKey={preferencesKey}
          userId={userId}
          userPreferences={userPreferences}
          maxHeight="100%"
          enableStickyHeader={true}
          enableColumnReorder={true}
          enableColumnResize={true}
          enableColumnVisibility={true}
          pagination={pagination}
          onLoadMore={onLoadMore}
          isLoading={isLoading}
          emptyState={<Alert variant="info" className="m-3">No commissionable invoices for this period.</Alert>}
        />
      </Card.Body>

      <InvoiceDetailModal
        show={!!invoiceModalId}
        onHide={() => setInvoiceModalId(null)}
        invoiceId={invoiceModalId}
        onInvoiceUpdated={(updated) => {
          if (invoiceQueryKey) {
            queryClient.setQueryData(invoiceQueryKey.concat([invoiceModalId]) as any, updated);
          } else {
            queryClient.setQueryData(['invoice', invoiceModalId], updated);
          }
          // Per-row commission_* fields come from a server-side JOIN that
          // the PATCH response doesn't carry, so a synchronous merge would
          // leave the row stale. Hand the parent a chance to invalidate.
          onInvoiceUpdated?.();
        }}
      />

      {selectedBankTxn && (
        <TransactionDetailModal
          key={selectedBankTxn.id}
          transaction={selectedBankTxn}
          onClose={() => setSelectedBankTxn(null)}
          onUpdated={() => {
            // A bank-txn override (memo edit) can change which commission
            // gets imported on next sync — bubble up so callers refetch.
            onInvoiceUpdated?.();
          }}
        />
      )}
    </Card>
  );
};

export default CommissionableInvoicesTable;
